import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Checkbox,
  Tooltip,
  Menu,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  Collapse,
  ListItemIcon,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Backup as BackupIcon,
  Storage as StorageIcon,
  AccessTime as AccessTimeIcon,
  Folder as FolderIcon,
  Verified as VerifiedIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Security as SecurityIcon,
  Restore as RestoreIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/axios';

interface BackupFile {
  filename: string;
  timestamp: string;
  size: number;
  type: 'database' | 'compressed' | 'comprehensive';
}

interface BackupStats {
  totalBackups: number;
  totalSize: number;
  oldestBackup: string | null;
  newestBackup: string | null;
}

interface BackupVerification {
  success: boolean;
  checksum: string;
  size: number;
  tables: string[];
  rowCounts: { [key: string]: number };
  errors?: string[];
  warnings?: string[];
}

interface BackupRestoration {
  success: boolean;
  errors?: string[];
  warnings?: string[];
}

interface BackupReport {
  verification: BackupVerification;
  restoration: BackupRestoration;
  summary: {
    timestamp: string;
    size: number;
    checksum: string;
    tableCount: number;
    totalRows: number;
    status: 'healthy' | 'warning' | 'error';
  };
}

interface BackupSchedule {
  schedule: string;
  type: 'full' | 'incremental';
  includeFiles: boolean;
  createdBy: string;
  createdAt: string;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDate(date: string | null): string {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString();
}

export default function BackupManagement() {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [backupToDelete, setBackupToDelete] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [selectedBackups, setSelectedBackups] = useState<string[]>([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBackup, setSelectedBackup] = useState<string | null>(null);
  const [verificationDialog, setVerificationDialog] = useState(false);
  const [selectedBackupReport, setSelectedBackupReport] = useState<BackupReport | null>(null);
  const [verifyingBackup, setVerifyingBackup] = useState(false);
  const [restoreDialog, setRestoreDialog] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(false);
  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [createBackupDialog, setCreateBackupDialog] = useState(false);
  const [backupConfig, setBackupConfig] = useState({
    type: 'full' as 'full' | 'incremental',
    includeFiles: true,
    description: ''
  });
  const [scheduleConfig, setScheduleConfig] = useState({
    schedule: '0 0 * * *', // Daily at midnight
    type: 'full' as 'full' | 'incremental',
    includeFiles: true
  });
  const queryClient = useQueryClient();

  const { data, isLoading, error: fetchError } = useQuery<{ backups: BackupFile[], stats: BackupStats }>({
    queryKey: ['backups'],
    queryFn: async () => {
      const response = await api.get('/backups');
      return response.data;
    }
  });

  const { data: scheduleData } = useQuery<{ schedule: BackupSchedule | null }>({
    queryKey: ['backup-schedule'],
    queryFn: async () => {
      const response = await api.get('/backups/schedule');
      return response.data;
    }
  });

  const createBackupMutation = useMutation({
    mutationFn: async (config: typeof backupConfig) => {
      const response = await api.post('/backups', config);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] });
      setSuccess('Backup created successfully');
      setCreateBackupDialog(false);
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to create backup');
    }
  });

  const deleteBackupMutation = useMutation({
    mutationFn: async (filename: string) => {
      await api.delete(`/backups/${filename}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] });
      setSuccess('Backup deleted successfully');
      setDeleteConfirmOpen(false);
      setBackupToDelete(null);
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to delete backup');
    }
  });

  const restoreBackupMutation = useMutation({
    mutationFn: async ({ filename, dryRun }: { filename: string; dryRun: boolean }) => {
      const response = await api.post(`/backups/${filename}/restore`, { dryRun });
      return response.data;
    },
    onSuccess: (data) => {
      if (data.dryRun) {
        setSuccess('Dry run completed - backup appears restorable');
      } else {
        setSuccess('Backup restored successfully');
      }
      setRestoreDialog(false);
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to restore backup');
    }
  });

  const scheduleBackupMutation = useMutation({
    mutationFn: async (config: typeof scheduleConfig) => {
      const response = await api.post('/backups/schedule', config);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-schedule'] });
      setSuccess('Backup schedule created successfully');
      setScheduleDialog(false);
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to schedule backup');
    }
  });

  const downloadBackup = async (filename: string) => {
    try {
      setDownloadProgress(0);
      const response = await api.get(`/backups/${filename}`, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          const progress = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setDownloadProgress(progress);
        }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSuccess('Backup downloaded successfully');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to download backup');
    } finally {
      setDownloadProgress(null);
    }
  };

  const downloadSelectedBackups = async () => {
    for (const filename of selectedBackups) {
      await downloadBackup(filename);
    }
    setSelectedBackups([]);
  };

  const handleDeleteClick = (filename: string) => {
    setBackupToDelete(filename);
    setDeleteConfirmOpen(true);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, filename: string) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedBackup(filename);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedBackup(null);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedBackups(data?.backups.map(b => b.filename) || []);
    } else {
      setSelectedBackups([]);
    }
  };

  const handleSelectBackup = (filename: string) => {
    setSelectedBackups(prev =>
      prev.includes(filename)
        ? prev.filter(f => f !== filename)
        : [...prev, filename]
    );
  };

  const verifyBackup = async (filename: string) => {
    try {
      setVerifyingBackup(true);
      const response = await api.get(`/backups/${filename}/report`);
      setSelectedBackupReport(response.data);
      setVerificationDialog(true);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to verify backup');
    } finally {
      setVerifyingBackup(false);
    }
  };

  const handleRestoreClick = (filename: string) => {
    setSelectedBackup(filename);
    setRestoreDialog(true);
  };

  const handleRestore = async (dryRun: boolean = false) => {
    if (!selectedBackup) return;
    
    setRestoringBackup(true);
    try {
      await restoreBackupMutation.mutateAsync({ filename: selectedBackup, dryRun });
    } finally {
      setRestoringBackup(false);
    }
  };

  const getStatusColor = (status: 'healthy' | 'warning' | 'error') => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: 'healthy' | 'warning' | 'error') => {
    switch (status) {
      case 'healthy':
        return <VerifiedIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'error':
        return <ErrorIcon />;
      default:
        return null;
    }
  };

  const getBackupTypeIcon = (type: string) => {
    switch (type) {
      case 'comprehensive':
        return <CloudUploadIcon />;
      case 'compressed':
        return <StorageIcon />;
      default:
        return <BackupIcon />;
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Alert severity="error">
        {fetchError instanceof Error ? fetchError.message : 'Failed to fetch backups'}
      </Alert>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Backup Management
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<BackupIcon />}
            onClick={() => setCreateBackupDialog(true)}
            disabled={createBackupMutation.isPending}
            sx={{ mr: 1 }}
          >
            Create Backup
          </Button>
          <Button
            variant="outlined"
            startIcon={<ScheduleIcon />}
            onClick={() => setScheduleDialog(true)}
            sx={{ mr: 1 }}
          >
            Schedule
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => queryClient.invalidateQueries({ queryKey: ['backups'] })}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {downloadProgress !== null && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <LinearProgress variant="determinate" value={downloadProgress} />
          <Typography variant="body2" color="text.secondary" align="center">
            Downloading... {downloadProgress}%
          </Typography>
        </Box>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <StorageIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Total Backups</Typography>
              </Box>
              <Typography variant="h4">{data?.stats.totalBackups || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <FolderIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Total Size</Typography>
              </Box>
              <Typography variant="h4">{formatFileSize(data?.stats.totalSize || 0)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AccessTimeIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Oldest Backup</Typography>
              </Box>
              <Typography variant="body1">{formatDate(data?.stats.oldestBackup)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AccessTimeIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Newest Backup</Typography>
              </Box>
              <Typography variant="body1">{formatDate(data?.stats.newestBackup)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {scheduleData?.schedule && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h6" gutterBottom>
                  Automated Backup Schedule
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Schedule: {scheduleData.schedule.schedule} | Type: {scheduleData.schedule.type} | 
                  Include Files: {scheduleData.schedule.includeFiles ? 'Yes' : 'No'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Created: {formatDate(scheduleData.schedule.createdAt)}
                </Typography>
              </Box>
              <Chip
                icon={<PlayArrowIcon />}
                label="Active"
                color="success"
                variant="outlined"
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {selectedBackups.length > 0 && (
        <Box mb={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={downloadSelectedBackups}
          >
            Download Selected ({selectedBackups.length})
          </Button>
        </Box>
      )}

      <Paper>
        <List>
          <ListItem>
            <Checkbox
              checked={selectedBackups.length === (data?.backups.length || 0)}
              indeterminate={selectedBackups.length > 0 && selectedBackups.length < (data?.backups.length || 0)}
              onChange={handleSelectAll}
            />
            <ListItemText
              primary="Filename"
              secondary="Size"
            />
            <ListItemText
              primary="Type"
              secondary="Date"
            />
            <ListItemSecondaryAction>
              <Typography variant="body2" color="text.secondary">
                Actions
              </Typography>
            </ListItemSecondaryAction>
          </ListItem>
          {data?.backups.map((backup) => (
            <ListItem key={backup.filename}>
              <Checkbox
                checked={selectedBackups.includes(backup.filename)}
                onChange={() => handleSelectBackup(backup.filename)}
              />
              <ListItemIcon>
                {getBackupTypeIcon(backup.type)}
              </ListItemIcon>
              <ListItemText
                primary={backup.filename}
                secondary={formatFileSize(backup.size)}
              />
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center">
                    <Chip
                      label={backup.type}
                      size="small"
                      color={backup.type === 'comprehensive' ? 'primary' : 'default'}
                      sx={{ mr: 1 }}
                    />
                  </Box>
                }
                secondary={new Date(backup.timestamp).toLocaleString()}
              />
              <ListItemSecondaryAction>
                <Tooltip title="Download">
                  <IconButton
                    edge="end"
                    onClick={() => downloadBackup(backup.filename)}
                    sx={{ mr: 1 }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="More options">
                  <IconButton
                    edge="end"
                    onClick={(e) => handleMenuOpen(e, backup.filename)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedBackup) {
            downloadBackup(selectedBackup);
          }
          handleMenuClose();
        }}>
          <DownloadIcon sx={{ mr: 1 }} /> Download
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedBackup) {
            verifyBackup(selectedBackup);
          }
          handleMenuClose();
        }}>
          <SecurityIcon sx={{ mr: 1 }} /> Verify
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedBackup) {
            handleRestoreClick(selectedBackup);
          }
          handleMenuClose();
        }}>
          <RestoreIcon sx={{ mr: 1 }} /> Restore
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedBackup) {
            handleDeleteClick(selectedBackup);
          }
          handleMenuClose();
        }}>
          <DeleteIcon sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Create Backup Dialog */}
      <Dialog
        open={createBackupDialog}
        onClose={() => setCreateBackupDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Backup</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Backup Type</InputLabel>
                <Select
                  value={backupConfig.type}
                  onChange={(e) => setBackupConfig(prev => ({ ...prev, type: e.target.value as 'full' | 'incremental' }))}
                  label="Backup Type"
                >
                  <MenuItem value="full">Full Backup</MenuItem>
                  <MenuItem value="incremental">Incremental Backup</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={backupConfig.includeFiles}
                    onChange={(e) => setBackupConfig(prev => ({ ...prev, includeFiles: e.target.checked }))}
                  />
                }
                label="Include Configuration Files"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (Optional)"
                value={backupConfig.description}
                onChange={(e) => setBackupConfig(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateBackupDialog(false)}>Cancel</Button>
          <Button
            onClick={() => createBackupMutation.mutate(backupConfig)}
            variant="contained"
            disabled={createBackupMutation.isPending}
          >
            Create Backup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Backup Dialog */}
      <Dialog
        open={scheduleDialog}
        onClose={() => setScheduleDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Schedule Automated Backup</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cron Expression"
                value={scheduleConfig.schedule}
                onChange={(e) => setScheduleConfig(prev => ({ ...prev, schedule: e.target.value }))}
                helperText="Example: 0 0 * * * (Daily at midnight)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Backup Type</InputLabel>
                <Select
                  value={scheduleConfig.type}
                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, type: e.target.value as 'full' | 'incremental' }))}
                  label="Backup Type"
                >
                  <MenuItem value="full">Full Backup</MenuItem>
                  <MenuItem value="incremental">Incremental Backup</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={scheduleConfig.includeFiles}
                    onChange={(e) => setScheduleConfig(prev => ({ ...prev, includeFiles: e.target.checked }))}
                  />
                }
                label="Include Configuration Files"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialog(false)}>Cancel</Button>
          <Button
            onClick={() => scheduleBackupMutation.mutate(scheduleConfig)}
            variant="contained"
            disabled={scheduleBackupMutation.isPending}
          >
            Schedule Backup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog
        open={restoreDialog}
        onClose={() => setRestoreDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Restore Backup</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to restore from backup: <strong>{selectedBackup}</strong>?
          </Typography>
          <Typography variant="body2" color="warning.main" gutterBottom>
            This will replace all current data. Make sure you have a backup of your current data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog(false)}>Cancel</Button>
          <Button
            onClick={() => handleRestore(true)}
            variant="outlined"
            disabled={restoringBackup}
          >
            Test Restore
          </Button>
          <Button
            onClick={() => handleRestore(false)}
            variant="contained"
            color="error"
            disabled={restoringBackup}
          >
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this backup? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={() => backupToDelete && deleteBackupMutation.mutate(backupToDelete)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verification Dialog */}
      <Dialog
        open={verificationDialog}
        onClose={() => setVerificationDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Backup Verification Report
          {selectedBackupReport && (
            <Chip
              icon={getStatusIcon(selectedBackupReport.summary.status)}
              label={selectedBackupReport.summary.status.toUpperCase()}
              color={getStatusColor(selectedBackupReport.summary.status)}
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedBackupReport && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Summary
                      </Typography>
                      <Typography>
                        Size: {formatFileSize(selectedBackupReport.summary.size)}
                      </Typography>
                      <Typography>
                        Tables: {selectedBackupReport.summary.tableCount}
                      </Typography>
                      <Typography>
                        Total Rows: {selectedBackupReport.summary.totalRows}
                      </Typography>
                      <Typography>
                        Checksum: {selectedBackupReport.summary.checksum}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Verification Status
                      </Typography>
                      <Typography color={selectedBackupReport.verification.success ? 'success.main' : 'error.main'}>
                        {selectedBackupReport.verification.success ? 'Success' : 'Failed'}
                      </Typography>
                      {selectedBackupReport.verification.errors?.map((error, index) => (
                        <Typography key={index} color="error" variant="body2">
                          {error}
                        </Typography>
                      ))}
                      {selectedBackupReport.verification.warnings?.map((warning, index) => (
                        <Typography key={index} color="warning.main" variant="body2">
                          {warning}
                        </Typography>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Restoration Test
                      </Typography>
                      <Typography color={selectedBackupReport.restoration.success ? 'success.main' : 'error.main'}>
                        {selectedBackupReport.restoration.success ? 'Success' : 'Failed'}
                      </Typography>
                      {selectedBackupReport.restoration.errors?.map((error, index) => (
                        <Typography key={index} color="error" variant="body2">
                          {error}
                        </Typography>
                      ))}
                      {selectedBackupReport.restoration.warnings?.map((warning, index) => (
                        <Typography key={index} color="warning.main" variant="body2">
                          {warning}
                        </Typography>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerificationDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 